(() => {
  if (window.MamamiCollectionLoading) return;

  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let seen = new WeakSet();
  const snapshots = new Map();
  let request;
  let scrollFrame;
  const container = () => document.querySelector('[data-collection-loading]');
  const snapshotKey = () => `mamami-collection-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    // Wait until enough of the image is on screen for the short reveal to be perceptible.
    const entering = entries.filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.18);
    const rows = new Map();
    entering.sort((a, b) => a.target.offsetTop - b.target.offsetTop || a.target.offsetLeft - b.target.offsetLeft);
    entering.forEach(({ target }) => {
      observer.unobserve(target);
      target.dataset.collectionRevealed = 'true';
      const row = target.offsetTop;
      const index = rows.get(row) || 0;
      rows.set(row, index + 1);
      if (!motion.matches && target.animate) {
        target.animate([
          { opacity: 0.35, transform: 'translateY(12px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 300, delay: index * 50, easing: 'ease-out', fill: 'backwards' });
      }
    });
  }, { threshold: 0.18 }) : null;

  function enhance() {
    const root = container();
    if (!root) return;
    root.querySelectorAll('modal-opener[data-modal]').forEach((opener) => {
      const modals = document.querySelectorAll(opener.dataset.modal);
      Array.from(modals).slice(1).forEach((modal) => modal.remove());
    });
    root.querySelectorAll('#product-grid > .grid__item').forEach((card) => {
      if (seen.has(card) || card.dataset.collectionRevealed) return;
      seen.add(card);
      if (observer) observer.observe(card);
    });
    const controls = root.querySelector('[data-load-more-controls]');
    const fallback = root.querySelector('[data-pagination-fallback]');
    if (controls && fallback && window.fetch) {
      controls.hidden = false;
      fallback.hidden = root.dataset.loadMoreError !== 'true';
    }
  }

  function save() {
    const root = container();
    if (!root) return;
    if (root.querySelector('.collection.loading')) return;
    const section = root.closest('.shopify-section');
    if (!section) return;
    const state = history.state || {};
    const key = state.mamamiCollection?.key || snapshotKey();
    const copy = section.cloneNode(true);
    const params = new URLSearchParams(location.search);
    section.querySelectorAll('input').forEach((input, index) => {
      const clone = copy.querySelectorAll('input')[index];
      const filter = input.name.startsWith('filter.');
      clone.setAttribute('value', filter && input.type === 'number' ? params.get(input.name) || '' : input.value);
      clone.toggleAttribute('checked', filter ? params.getAll(input.name).includes(input.value) : input.checked);
    });
    section.querySelectorAll('select').forEach((select, index) => {
      const clone = copy.querySelectorAll('select')[index];
      const value = select.name === 'sort_by' ? params.get('sort_by') || root.dataset.defaultSort : select.value;
      Array.from(clone.options).forEach((option) => option.toggleAttribute('selected', option.value === value));
    });
    copy.querySelectorAll('.loading').forEach((element) => element.classList.remove('loading'));
    copy.querySelectorAll('[aria-busy]').forEach((element) => element.removeAttribute('aria-busy'));
    copy.querySelectorAll('[data-load-more]').forEach((element) => element.removeAttribute('aria-disabled'));
    const modals = Array.from(section.querySelectorAll('modal-opener[data-modal]')).map((opener) => {
      const modal = document.querySelector(opener.dataset.modal)?.cloneNode(true);
      if (!modal) return '';
      modal.removeAttribute('open');
      modal.querySelector('.quick-add-modal__content-info').innerHTML = '';
      return modal.outerHTML;
    }).join('');
    const snapshot = { url: location.href, html: copy.outerHTML, modals };
    snapshots.set(key, snapshot);
    let inline;
    try {
      sessionStorage.setItem(key, JSON.stringify(snapshot));
    } catch (_) {
      inline = snapshot;
    }
    history.replaceState({ ...state, mamamiCollection: { key, x: scrollX, y: scrollY, inline } }, '', location.href);
  }

  function cancel() {
    if (request) request.abort();
    request = null;
    observer?.disconnect();
    seen = new WeakSet();
    if (container()) {
      delete container().dataset.loadMoreError;
      container().removeAttribute('aria-busy');
    }
  }

  function restore(state) {
    const saved = state?.mamamiCollection;
    if (!container() || !saved) return false;
    let snapshot = snapshots.get(saved.key) || saved.inline;
    try {
      snapshot ||= JSON.parse(sessionStorage.getItem(saved.key));
    } catch (_) {}
    if (!snapshot || snapshot.url !== location.href || typeof FacetFiltersForm === 'undefined') return false;
    cancel();
    FacetFiltersForm.cancelRequest();
    const dialogs = new DOMParser().parseFromString(snapshot.modals || '', 'text/html');
    dialogs.querySelectorAll('quick-add-modal').forEach((modal) => {
      if (!document.getElementById(modal.id)) document.body.appendChild(document.importNode(modal, true));
    });
    FacetFiltersForm.searchParamsPrev = location.search.slice(1);
    FacetFiltersForm.renderFilters(snapshot.html);
    FacetFiltersForm.renderProductGridContainer(snapshot.html);
    FacetFiltersForm.renderProductCount(snapshot.html);
    const html = new DOMParser().parseFromString(snapshot.html, 'text/html');
    html.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      const current = document.getElementById(select.id);
      if (current) current.value = select.value;
    });
    enhance();
    requestAnimationFrame(() => window.scrollTo(saved.x, saved.y));
    return true;
  }

  async function loadMore(link) {
    if (request || link.getAttribute('aria-disabled') === 'true') return;
    const root = container();
    const status = root.querySelector('[data-load-more-status]');
    const url = new URL(link.href);
    const active = new URL(location.href);
    active.searchParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'section_id' && !url.searchParams.has(key)) url.searchParams.append(key, value);
    });
    url.searchParams.delete('section_id');
    save();
    const controller = new AbortController();
    request = controller;
    link.setAttribute('aria-disabled', 'true');
    root.setAttribute('aria-busy', 'true');
    status.textContent = status.dataset.loadingText;
    const fetchURL = new URL(url);
    fetchURL.searchParams.set('section_id', root.querySelector('#product-grid').dataset.id);
    try {
      const response = await fetch(fetchURL, { signal: controller.signal });
      if (!response.ok) throw new Error('Collection request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || root !== container()) return;
      const grid = root.querySelector('#product-grid');
      const incoming = html.querySelector('[data-collection-loading] #product-grid');
      const pagination = html.querySelector('[data-collection-pagination]');
      if (!incoming || !pagination || !incoming.children.length) throw new Error('Missing collection results');
      const cards = Array.from(incoming.children);
      grid.append(...cards);
      delete root.dataset.loadMoreError;
      root.querySelector('[data-collection-pagination]').replaceWith(pagination);
      history.pushState({ searchParams: url.search.slice(1) }, '', url);
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = url.search.slice(1);
      enhance();
      const message = root.querySelector('[data-load-more-status]');
      message.textContent = message.dataset.loadedText.replace('[count]', grid.children.length);
      Array.from(cards[0].querySelectorAll('.card__heading a')).find((anchor) => anchor.getClientRects().length)?.focus({ preventScroll: true });
      root.removeAttribute('aria-busy');
      save();
    } catch (error) {
      if (error.name !== 'AbortError' && root === container()) {
        status.textContent = status.dataset.errorText;
        root.dataset.loadMoreError = 'true';
        root.querySelector('[data-pagination-fallback]').hidden = false;
      }
    } finally {
      link.removeAttribute('aria-disabled');
      if (request === controller) {
        root.removeAttribute('aria-busy');
        request = null;
      }
    }
  }

  window.MamamiCollectionLoading = { enhance, save, cancel, restore };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-load-more]');
    if (!link || !container()?.contains(link) || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    loadMore(link);
  });

  window.addEventListener('scroll', () => {
    if (scrollFrame || !container()) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;
      const state = history.state;
      if (state?.mamamiCollection) {
        history.replaceState({ ...state, mamamiCollection: { ...state.mamamiCollection, x: scrollX, y: scrollY } }, '', location.href);
      }
    });
  }, { passive: true });
  window.addEventListener('pagehide', save);
  document.addEventListener('shopify:section:load', enhance);
  motion.addEventListener('change', () => {
    if (motion.matches) container()?.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  });
  document.addEventListener('DOMContentLoaded', () => {
    if (!restore(history.state)) {
      enhance();
      save();
    }
    const root = container();
    if (root) new MutationObserver(enhance).observe(root, { childList: true, subtree: true });
  }, { once: true });
})();
